import { Base64 } from "js-base64";

/**
 * Sends a request to receive an OTP for registration or verification and stores it in sessionStorage.
 *
 * - Endpoint: `/auth/varify-otp`
 * - Encodes the OTP in Base64 and saves it as `regotp` in `sessionStorage`.
 *
 * @async
 * @function GetOtpForRes
 * @param {string} email - The email address to send the OTP to.
 * @returns {Promise<{
 *   data: string | null,
 *   error: boolean,
 *   msg?: string
 * }>} A promise resolving to an object:
 * - `data`: OTP string (decoded) if success, `null` if failure.
 * - `error`: `true` if any error occurred, otherwise `false`.
 * - `msg` (optional): Human-readable message for logging or display.
 *
 * @example
 * const response = await GetOtpForRes("test@example.com");
 * if (!response.error) {
 *   console.log("OTP received:", response.data);
 * } else {
 *   alert(response.msg);
 * }
 */

const GetOtpForRes = async (email) => {
  const domain = process.env.REACT_APP_SERVER;
  const url = `${domain}/auth/varify-otp`;

  const options = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email }),
  };

  try {
    const res = await fetch(url, options);
    const data = await res.json();

    if (res.status === 200) {
      if (data?.otp) {
        const encodedOtp = Base64.encode(data.otp.toString());
        sessionStorage.setItem("regotp", encodedOtp);
      }
      return { data: data?.otp, error: false };
    }

    // ✅ Fix: return an object with `data` key for consistency
    return { data: data?.message || "Unable to send OTP", error: true };
  } catch (err) {
    console.error("Network error in GetOtpForRes:", err);
    return { data: "Network error", error: true };
  }
};

export default GetOtpForRes;
